import { useMemo, useState } from 'react'
import { Chip, Dropdown, Spinner, Table, ToggleButton } from '@heroui/react'

import { ModuleIntro } from '@/components/layout'
import { EmptyState, ErrorState, LoadingState, Pagination } from '@/components/ui/data-state'
import { CheckIcon, LockIcon, MoreIcon, PlusIcon } from '@/components/ui/icons'
import type { TenantBranch } from '@/features/business'

import {
  BRANCH_PAGE_SIZE,
  BRANCH_SEARCH_DEBOUNCE_MS,
  isBranchKnown,
  isOnlyBranchOf,
  isSoldAt,
} from '../branch-links'
import { useBranchLinks, useBranchProductCounts, useDebouncedValue, useProducts } from '../hooks'
import { translated } from '../types'
import type { ProductListQuery, ProductSummary } from '../types'

import {
  BranchProductCell,
  BranchProductSearch,
  BranchRemovalConfirm,
  BranchWriteError,
} from './branch-product-parts'

interface BranchProductsGridProps {
  /** Every branch the tenant has — at most `BRANCH_GRID_MAX` of them. */
  branches: TenantBranch[]
}

interface GridColumn {
  id: string
  name: string
}

type BranchLinks = ReturnType<typeof useBranchLinks>

/**
 * Products down the side, branches across the top, a pill where they meet.
 *
 * The whole picture at once: a gap — the airport has no lattes — is something
 * the merchant sees rather than something they find by visiting each branch in
 * turn. Pressing "+ Add" sells the product at that branch too and leaves its
 * others alone; pressing "Sold" takes only that branch away.
 *
 * Pills rather than checkboxes because both states have to be legible at a
 * glance across a page of them. An empty checkbox on a white row reads as a
 * blank cell, and a locked one as a glitch; "+ Add" and "Only" say what they are.
 */
export function BranchProductsGrid({ branches }: BranchProductsGridProps) {
  const [search, setSearch] = useState('')
  const searchText = useDebouncedValue(search.trim(), BRANCH_SEARCH_DEBOUNCE_MS)
  const [page, setPage] = useState(0)

  // A new search can strand the merchant on a page that no longer exists.
  // Reset during render rather than in an effect, so no frame shows it.
  const [lastSearchText, setLastSearchText] = useState(searchText)

  if (searchText !== lastSearchText) {
    setLastSearchText(searchText)
    setPage(0)
  }

  const query = useMemo<ProductListQuery>(
    () => ({
      limit: BRANCH_PAGE_SIZE,
      offset: page * BRANCH_PAGE_SIZE,
      searchText: searchText || undefined,
    }),
    [page, searchText],
  )

  const { products, total, isLoading, isRefreshing, error, retry } = useProducts(query)
  const catalogue = useProducts({ limit: 1, offset: 0 })
  const counts = useBranchProductCounts(branches.map((branch) => branch.id))
  const links = useBranchLinks()

  const columns: GridColumn[] = branches.map((branch) => ({
    id: branch.id,
    name: translated(branch.name, 'Branch'),
  }))

  return (
    <>
      <ModuleIntro description="Choose where each product is sold. A product's only branch is locked, so it is always sold somewhere." />

      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex w-full sm:max-w-sm">
          <BranchProductSearch value={search} onChange={setSearch} />
        </div>

        {!catalogue.isLoading && (
          <p className="text-muted text-xs tabular-nums">
            {catalogue.total} products · {branches.length}{' '}
            {branches.length === 1 ? 'branch' : 'branches'}
          </p>
        )}
      </div>

      <BranchWriteError error={links.error} />

      {isLoading ? (
        <LoadingState />
      ) : error ? (
        <ErrorState subject="your products" error={error} onRetry={retry} />
      ) : products.length === 0 ? (
        <EmptyState
          title={searchText ? 'Nothing matches.' : 'No products yet.'}
          description={
            searchText
              ? 'Try a broader search, or clear it to see every product.'
              : 'Add products on the Products tab, then choose here where each one is sold.'
          }
          action={searchText ? { label: 'Clear search', onPress: () => setSearch('') } : undefined}
        />
      ) : (
        <>
          {/* `brand-soft-scope` makes the "Sold" and "Only" tints brand purple
              rather than HeroUI's blue — see globals.css. */}
          <div
            className={
              isRefreshing ? 'brand-soft-scope opacity-60 transition-opacity' : 'brand-soft-scope'
            }
          >
            <Table>
              <Table.ScrollContainer>
                <Table.Content aria-label="Where each product is sold">
                  <Table.Header>
                    <Table.Column id="product" isRowHeader className="min-w-56">
                      Product
                    </Table.Column>

                    {columns.map((branch) => (
                      <Table.Column key={branch.id} id={branch.id} className="w-32">
                        <BranchHeader
                          branch={branch}
                          products={products}
                          links={links}
                          sold={counts[branch.id]}
                          total={catalogue.isLoading ? undefined : catalogue.total}
                        />
                      </Table.Column>
                    ))}
                  </Table.Header>

                  <Table.Body>
                    {products.map((product) => (
                      <Table.Row key={product.id} id={product.id}>
                        <Table.Cell>
                          <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
                            <BranchProductCell product={product} />
                            {product.branches?.length === 0 && (
                              <Chip size="sm" color="warning" variant="soft">
                                Not sold anywhere
                              </Chip>
                            )}
                          </span>
                        </Table.Cell>

                        {columns.map((branch) => (
                          <Table.Cell key={branch.id}>
                            <span className="flex justify-center">
                              <BranchPill product={product} branch={branch} links={links} />
                            </span>
                          </Table.Cell>
                        ))}
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table.Content>
              </Table.ScrollContainer>
            </Table>
          </div>

          <Pagination
            page={page}
            pageSize={BRANCH_PAGE_SIZE}
            total={total}
            onPageChange={setPage}
          />
        </>
      )}

      <BranchRemovalConfirm
        removal={links.removal}
        onConfirm={links.confirmRemoval}
        onCancel={links.cancelRemoval}
      />
    </>
  )
}

/**
 * A branch's column head: its name, how much of the catalogue it sells, and a
 * menu for the visible page.
 *
 * The page actions sit behind ••• rather than in a header checkbox. A tri-state
 * box over every column read as four warnings whenever a page was mixed — which
 * is nearly always — and nothing about it said it acted on a whole page.
 */
function BranchHeader({
  branch,
  products,
  links,
  sold,
  total,
}: {
  branch: GridColumn
  products: ProductSummary[]
  links: BranchLinks
  sold: number | undefined
  total: number | undefined
}) {
  const isIdle = (product: ProductSummary) => !links.isPending(product.id, branch.id)
  const known = products.filter(isBranchKnown)
  const addable = known.filter((product) => !isSoldAt(product, branch.id) && isIdle(product))
  const removable = known.filter(
    (product) =>
      isSoldAt(product, branch.id) && !isOnlyBranchOf(product, branch.id) && isIdle(product),
  )

  return (
    <span className="flex flex-col items-center gap-1.5 text-center">
      <span className="flex max-w-32 items-center gap-0.5">
        <span className="truncate" title={branch.name}>
          {branch.name}
        </span>

        <Dropdown>
          <Dropdown.Trigger
            aria-label={`Page actions for ${branch.name}`}
            className="text-muted hover:text-foreground hover:bg-default inline-flex size-6 shrink-0 items-center justify-center rounded-full transition-colors"
          >
            <MoreIcon width={14} height={14} />
          </Dropdown.Trigger>
          <Dropdown.Popover>
            <Dropdown.Menu
              aria-label={`Page actions for ${branch.name}`}
              disabledKeys={[
                ...(addable.length === 0 ? ['add'] : []),
                ...(removable.length === 0 ? ['remove'] : []),
              ]}
              onAction={(key) =>
                key === 'add'
                  ? links.link(addable, branch.id)
                  : links.unlink(removable, branch.id, branch.name)
              }
            >
              <Dropdown.Item id="add">
                {addable.length > 0
                  ? `Add ${addable.length} on this page`
                  : 'Everything on this page is sold here'}
              </Dropdown.Item>
              <Dropdown.Item id="remove">
                {removable.length > 0
                  ? `Remove ${removable.length} on this page`
                  : 'Nothing on this page to remove'}
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown.Popover>
        </Dropdown>
      </span>

      <CoverageBar sold={sold} total={total} />
    </span>
  )
}

/** "17 / 24" over a thin bar — how much of the catalogue a branch sells. */
function CoverageBar({ sold, total }: { sold: number | undefined; total: number | undefined }) {
  if (sold === undefined || total === undefined) {
    return <span className="text-muted text-[11px] font-normal">…</span>
  }

  const percent = total > 0 ? Math.round((sold / total) * 100) : 0

  return (
    <span className="flex flex-col items-center gap-1">
      <span className="text-muted text-[11px] font-normal tabular-nums">
        {sold} / {total}
      </span>
      <span className="bg-default block h-1 w-16 overflow-hidden rounded-full" aria-hidden>
        {/* The one inline style: the fill's width is data, not design. */}
        <span className="bg-accent block h-full rounded-full" style={{ width: `${percent}%` }} />
      </span>
    </span>
  )
}

/**
 * One product at one branch.
 *
 * Three looks for three answers: "Sold" filled, "+ Add" outlined, and "Only"
 * with a lock when this is the product's last branch — taking it away would
 * leave the product on no till, which the server refuses.
 */
function BranchPill({
  product,
  branch,
  links,
}: {
  product: ProductSummary
  branch: GridColumn
  links: BranchLinks
}) {
  // A row whose outlets did not arrive is not known either way — see `isBranchKnown`.
  if (!isBranchKnown(product)) return <span className="text-muted text-xs">—</span>

  const productName = translated(product.name)

  if (isOnlyBranchOf(product, branch.id)) {
    return (
      <span
        title="Its only branch — add another first"
        className="bg-accent-soft text-accent-soft-foreground inline-flex h-7 min-w-20 items-center justify-center gap-1 rounded-full px-3 text-xs font-medium"
      >
        <LockIcon width={12} height={12} />
        Only
        <span className="sr-only">
          {` — ${productName} is sold only at ${branch.name}. Add another branch first.`}
        </span>
      </span>
    )
  }

  const isSold = isSoldAt(product, branch.id)
  const isPending = links.isPending(product.id, branch.id)

  return (
    <ToggleButton
      size="sm"
      aria-label={`Sell ${productName} at ${branch.name}`}
      isSelected={isSold}
      isDisabled={isPending}
      onChange={(isSelected) =>
        isSelected
          ? links.link([product], branch.id)
          : links.unlink([product], branch.id, branch.name)
      }
      // Unselected is drawn as an outline rather than HeroUI's grey fill, so a
      // row of "+ Add" stays quiet beside the filled "Sold" ones it sits among.
      className="border-separator hover:border-accent hover:text-accent h-7 min-w-20 gap-1 rounded-full border border-dashed px-3 text-xs [--toggle-button-bg-hover:transparent] [--toggle-button-bg:transparent] data-[selected=true]:border-solid data-[selected=true]:border-transparent data-[selected=true]:hover:text-current"
    >
      {isPending ? (
        <Spinner size="sm" />
      ) : isSold ? (
        <>
          <CheckIcon width={12} height={12} />
          Sold
        </>
      ) : (
        <>
          <PlusIcon width={12} height={12} />
          Add
        </>
      )}
    </ToggleButton>
  )
}
